import { useCallback, useEffect, useMemo, useState } from 'react';

import { countDimChildRowsForIndicator } from './builderArrowDetailRows';
import {
  clearBuilderArrowDimWindow,
  initBuilderArrowDimWindow,
} from './builderArrowDetailWindowStore';
import { syncArrowVirtualWindowsToScrollTop } from './builderArrowVirtualScroll';
import { getBuilderBlockDetailMeta, getBuilderBlockDetailTable } from './builderBlockDetailStore';
import { listParentIndicatorIds } from './mergeArrowVirtualDimTableData';
import useBuilderArrowVirtualTableData from './useBuilderArrowVirtualTableData';

type ChevronChange = {
  rowId?: string | number;
  newCell?: { isExpanded?: boolean };
  previousCell?: { isExpanded?: boolean };
};

type UseBuilderArrowOutputsIntegrationArguments = {
  enabled: boolean;
  indicatorValues: any[] | null | undefined;
  blockId: string | undefined;
  scenarioId: number | undefined;
  includeFooterRow: boolean;
};

/**
 * Arrow multipart fetch + virtual dim rows (shared by OpzV2 and ModernDataTable).
 */
export default function useBuilderArrowOutputsIntegration({
  enabled,
  indicatorValues,
  blockId,
  scenarioId,
  includeFooterRow,
}: UseBuilderArrowOutputsIntegrationArguments) {
  const [expandedArrowParentIds, setExpandedArrowParentIds] = useState<Set<number>>(
    () => new Set(),
  );
  const [arrowVirtualRevision, setArrowVirtualRevision] = useState(0);

  const bumpArrowVirtualRevision = useCallback(() => {
    setArrowVirtualRevision((value) => value + 1);
  }, []);

  useEffect(() => {
    setExpandedArrowParentIds(new Set());
    setArrowVirtualRevision((value) => value + 1);
  }, [blockId, scenarioId]);

  const virtualIndicatorValues = useBuilderArrowVirtualTableData({
    enabled,
    indicatorValues,
    expandedParentIds: expandedArrowParentIds,
    blockId,
    scenarioId,
    revision: arrowVirtualRevision,
  });

  const effectiveIndicatorValues = enabled ? virtualIndicatorValues : indicatorValues;

  const parentIdsForScroll = useMemo(() => {
    const parentsOnly = (indicatorValues ?? []).filter(
      (row: { chevronType?: string }) =>
        row?.chevronType !== 'child' && row?.chevronType !== 'arrow-spacer',
    );
    return listParentIndicatorIds(parentsOnly);
  }, [indicatorValues]);

  const handleTableScroll = useCallback(
    (scrollTop: number) => {
      if (!enabled || expandedArrowParentIds.size === 0) {
        return;
      }
      const changed = syncArrowVirtualWindowsToScrollTop(
        scrollTop,
        parentIdsForScroll,
        expandedArrowParentIds,
        includeFooterRow,
      );
      if (changed) {
        bumpArrowVirtualRevision();
      }
    },
    [
      bumpArrowVirtualRevision,
      enabled,
      expandedArrowParentIds,
      includeFooterRow,
      parentIdsForScroll,
    ],
  );

  const handleArrowChevronChange = useCallback(
    (change: ChevronChange) => {
      if (!enabled || !blockId || !scenarioId) {
        return;
      }

      const expanding = change?.newCell?.isExpanded && !change?.previousCell?.isExpanded;
      const collapsing = !change?.newCell?.isExpanded && change?.previousCell?.isExpanded;
      const parentId = Number(change.rowId);

      if (collapsing) {
        clearBuilderArrowDimWindow(parentId);
        setExpandedArrowParentIds((previous) => {
          const next = new Set(previous);
          next.delete(parentId);
          return next;
        });
        bumpArrowVirtualRevision();
        return;
      }

      if (!expanding) {
        return;
      }

      const indicatorRow = (indicatorValues ?? []).find(
        (row: { id?: number; chevronType?: string }) =>
          Number(row?.id) === parentId && row?.chevronType === 'parent',
      );
      const detailTable = getBuilderBlockDetailTable(blockId, scenarioId);
      const detailMeta = getBuilderBlockDetailMeta(blockId, scenarioId);
      if (!indicatorRow || !detailTable || !detailMeta?.dimColumns?.length) {
        return;
      }

      const totalCount = countDimChildRowsForIndicator(
        detailTable,
        detailMeta.indicatorColumn,
        indicatorRow.name,
      );
      initBuilderArrowDimWindow(parentId, indicatorRow.name, totalCount);
      setExpandedArrowParentIds((previous) => new Set(previous).add(parentId));
      bumpArrowVirtualRevision();
    },
    [blockId, bumpArrowVirtualRevision, enabled, indicatorValues, scenarioId],
  );

  return {
    effectiveIndicatorValues,
    handleTableScroll,
    handleArrowChevronChange,
  };
}
